/**
 * Reset Password Page Loader
 * Validates reset token and tracks page load
 */

import { type LoaderFunctionArgs } from 'react-router-dom';
import {
  trackRouteLoad,
  trackRouteError,
  startRouteTimer,
  calculateDataSize,
} from '@/utils/route-analytics';

export interface ResetPasswordData {
  token?: string;
  email?: string;
  isValidToken: boolean;
  errorMessage?: string;
}

/**
 * Reset Password loader function
 * Validates the reset token from URL params
 */
export async function resetPasswordLoader(args: LoaderFunctionArgs) {
  const getElapsed = startRouteTimer();

  try {
    // Get reset token and email from search params
    const url = new URL(args.request.url);

    let token: string | undefined;
    let email: string | undefined;

    // Check if data is passed as base64-encoded JSON (new format)
    const dataParam = url.searchParams.get('data');
    if (dataParam) {
      try {
        // Decode base64 and parse JSON
        const decodedData = atob(dataParam);
        const parsedData = JSON.parse(decodedData);
        token = parsedData.token;
        email = parsedData.email;
      } catch (decodeError) {
        console.error('Failed to decode data parameter:', decodeError);
        // Fall back to checking individual params
        token = url.searchParams.get('token') ?? undefined;
        email = url.searchParams.get('email') ?? undefined;
      }
    } else {
      // Check for individual token and email params (old format)
      token = url.searchParams.get('token') ?? undefined;
      email = url.searchParams.get('email') ?? undefined;
    }

    // Don't redirect when the token is missing — the page itself renders an
    // "Invalid Link" branch (with a "Request New Reset Link" button that
    // sends the user to /forgot-password). Redirecting here hid that UI and
    // made the page look like it was broken when visited directly.
    const isValidToken = !!token;

    const data: ResetPasswordData = {
      token,
      email,
      isValidToken,
    };

    // Track performance
    const loadTime = getElapsed();
    const dataSize = calculateDataSize(data);
    trackRouteLoad('/reset-password', loadTime, dataSize);

    // Return data directly (React Router loaders don't need to be wrapped)
    return data;
  } catch (error) {
    // If it's a redirect Response, let it propagate
    if (error instanceof Response) {
      throw error;
    }

    trackRouteError('/reset-password', error as Error);
    throw error;
  }
}
